import React, { FC, useEffect, useRef, useState } from "react";

import CustomGrid from "ui/grid/CustomGrid";
import {
	GridCellProps,
	GridColumnProps,
	GridItemChangeEvent,
} from "@progress/kendo-react-grid";
import CustomButton from "ui/Button";
import { CLASSNAME, COLOR, ICON_NAMES } from "utils/constant";
import { CustomGridCommandCell } from "ui/grid/GridCommandCell";
import { Comment } from "../utils";
import {
	deleteNoteHeadComment,
	getSalesNoteHeadCommentsApi,
	saveNoteHeadComments,
} from "api/salesNote";
import { t } from "i18next";
import { useInfoStore } from "store/error";
import { TextEditCell } from "ui/grid/TextEditCell";
import StepThreeMobileList from "./StepThreeMobileList";
import CustomModal from "ui/modal/Modal";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { useUserStore } from "store/index";

const LineOptions = [
	"Salg gjennomført og fisk frigitt fra lager",
	"EKSMVA bestillingsseddel",
	"Til destruering",
	"Tineprøver",
	"Htag",
];
interface HeadComment extends Comment {
	inEdit?: boolean;
}
export const FormStepThree: FC<{ noteHeaderId: number }> = ({
	noteHeaderId,
}) => {
	const [noteHeadComments, setNoteHeadComments] = useState<HeadComment[]>([]);
	const [isMobileView, setIsMobileView] = useState<boolean>(
		typeof window !== "undefined" ? window.innerWidth < 768 : false,
	);

	const deletingLineRef = useRef<HeadComment>();
	const [showDeleteLine, setShowDeleteLine] = useState(false);
	const { setError } = useInfoStore();
	const { roles, isAdmin } = useUserStore();

	useEffect(() => {
		const handleResize = () => setIsMobileView(window.innerWidth < 768);
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		getData();
	}, []);

	const handleOnChangeItem = (e: GridItemChangeEvent) => {
		const item = e.dataItem as HeadComment;
		const description = String(e.value ?? "");
		updateDescriptionLocal(item.id, description);
		updateItem(description, item.id);
	};

	const updateDescriptionLocal = (id: number, description: string) => {
		setNoteHeadComments((prev) =>
			prev.map((line) => (line.id === id ? { ...line, description } : line)),
		);
	};

	const handleMobileDescriptionChange = (id: number, description: string) => {
		updateDescriptionLocal(id, description);
	};

	const handleMobileDescriptionBlur = (id: number, description: string) => {
		updateItem(description, id);
	};

	const startDeletingLine = (line: HeadComment) => {
		deletingLineRef.current = line;
		setShowDeleteLine(true);
	};

	const handleDeleteItem = (data: GridCellProps) => {
		startDeletingLine(data.dataItem as HeadComment);
	};

	const deleteLine = () => {
		if (!deletingLineRef.current) return;
		deleteNoteHeadComment(deletingLineRef.current.id)
			.then((response) => {
				getData();
				setShowDeleteLine(false);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const adminUser = isAdmin && roles?.some((r) => r === "Admin");

	const handleOptionsFilter = (): string[] => {
		if (adminUser) {
			return LineOptions;
		}
		return LineOptions.filter((option) => option === "Htag");
	};

	const gridItems: GridColumnProps[] = [
		{
			field: "lineNumber",
			title: "Lnr",
			width: "40px",
			className: ` no-border`,
			editable: false,
		},
		{
			field: "description",
			title: "Notat",
			editable: true,
			className: ` no-border`,
			headerClassName: "table-text",
			//cells: {
			//	data: (props) => {
			//		return (
			//			<td className="no-border">
			//				<ComboBox
			//					data={handleOptionsFilter()}
			//					value={props.dataItem.description}
			//					onChange={(e) => {
			//						if (!props.onChange) return;
			//						props.onChange({
			//							dataItem: props.dataItem,
			//							dataIndex: props.dataIndex,
			//							field: props.field,
			//							value: e.value,
			//							syntheticEvent: e.syntheticEvent,
			//						});
			//					}}
			//					allowCustom={true}
			//				/>
			//			</td>
			//		);
			//	},
			//},
			cells: {
				data: (props) => (
					<TextEditCell
						{...props}
						field="description"
					/>
				),
			},
		},
		{
			field: "",
			title: "",
			cells: {
				data: (props) => (
					<CustomGridCommandCell
						{...props}
						style={{ display: "flex", justifyContent: "flex-end" }}
						onclick={handleDeleteItem}
						iconName={ICON_NAMES.Delete}
					/>
				),
			},
			width: "60px",
		},
	];

	const getData = () => {
		getSalesNoteHeadCommentsApi(noteHeaderId)
			.then((response) => {
				setNoteHeadComments(
					response.data.map((line) => ({ ...line, inEdit: true })),
				);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const addNewItem = () => {
		saveNoteHeadComments({ id: 0, noteHeaderId, description: "" })
			.then((response) => {
				getData();
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const updateItem = (description: string, id: number) => {
		saveNoteHeadComments({ id, noteHeaderId, description })
			.then((response) => {})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	return (
		<div>
			{isMobileView ? (
				<StepThreeMobileList
					comments={noteHeadComments}
					onDescriptionChange={handleMobileDescriptionChange}
					onDescriptionBlur={handleMobileDescriptionBlur}
					onDelete={startDeletingLine}
				/>
			) : (
				<CustomGrid
					gridColumns={gridItems}
					data={noteHeadComments}
					edit={{ id: true }}
					className={CLASSNAME.TableNumeric + " " + CLASSNAME.HideOverFlow}
					onItemChange={handleOnChangeItem}
				/>
			)}
			<div style={{ marginTop: "20px" }}>
				<CustomButton
					label={t("NewLine") + " +"}
					onClick={addNewItem}
					bgColor={COLOR.Green}
					color="white"
				/>
			</div>

			{showDeleteLine && (
				<CustomModal
					title={t("DeleteLine")}
					onClose={() => setShowDeleteLine(false)}
					actions={[
						{
							label: t("Cancel"),
							onClick: () => setShowDeleteLine(false),
							bgColor: COLOR.Green,
							color: "white",
						},
						{
							label: t("Yes"),
							onClick: deleteLine,
							bgColor: COLOR.StatusRed,
							color: "white",
						},
					]}
				>
					<p style={{ padding: "16px 0" }}>{t("DeleteLineConfirmation")}</p>
				</CustomModal>
			)}
		</div>
	);
};

export default FormStepThree;
